import React, { useState, useEffect } from 'react';
import '../../../App.scss';
import 'antd/dist/antd.css';
import { PageHeader, Table, Button, Space, DatePicker, Tag, Avatar } from 'antd';
import { UserOutlined, TeamOutlined, CalendarOutlined } from '@ant-design/icons';
import moment from 'moment';

import { useTeamLeavers } from '../../../models/useTeamLeavers';
import { ClientsSearch } from '../../../components/admin/AdminClientsComponents';
import CenteredSpinner from '../../../components/CenteredSpinner';
import { getAdminDatePresets, getCurrentWeekRange } from '../../../utils/adminDatePresets';
import { getAppBaseUrl } from '../../../utils/URLBuilder';

const { RangePicker } = DatePicker;
const LIMIT = 400;

const URLPath = getAppBaseUrl();

function escapeCsvValue(value) {
    const text = value === null || value === undefined ? '' : `${value}`;
    if (text.includes('"') || text.includes(',') || text.includes('\n')) {
        return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
}

function toIsoString(ts) {
    if (!ts) return '';
    if (typeof ts.toDate === 'function') return ts.toDate().toISOString();
    if (typeof ts.toMillis === 'function') return new Date(ts.toMillis()).toISOString();
    if (ts instanceof Date) return ts.toISOString();
    return '';
}

const ROLE_COLORS = { owner: 'red', admin: 'gold', member: 'blue', manager: 'purple' };
const TIP_BEHAVIOUR_COLORS = { both: 'blue', members: 'green', team: 'purple', pooled: 'purple' };

export default function AdminTeamLeaversScreen({ locStrings, isAdmin }) {
    const [dateRange, setDateRange] = useState(getCurrentWeekRange);
    const [, setPresetRefresh] = useState(0);
    const startDate = dateRange && dateRange[0] ? dateRange[0].toDate() : null;
    const endDate = dateRange && dateRange[1] ? dateRange[1].toDate() : null;

    const [loading, leavers] = useTeamLeavers({ startDate, endDate, limit: LIMIT });
    const [searchText, setSearchText] = useState('');
    const [filteredLeavers, setFilteredLeavers] = useState(null);

    useEffect(() => {
        const words = (searchText || '').toLowerCase().trim().split(/\s+/).filter(Boolean);
        if (words.length === 0) {
            setFilteredLeavers(null);
            return;
        }
        const filtered = (leavers || []).filter((leaver) => {
            const rolesStr = Array.isArray(leaver.roles) ? leaver.roles.join(' ') : '';
            const isOwner = leaver.clientId && leaver.teamOwnerClientId && leaver.clientId === leaver.teamOwnerClientId;
            const ownerStr = isOwner ? 'owner' : '';
            const tapLabel = leaver.teamTapBehaviour === 'team' ? 'pooled' : leaver.teamTapBehaviour;
            const haystack = [
                leaver.clientId,
                leaver.memberId,
                leaver.teamId,
                leaver.teamName,
                leaver.displayName,
                leaver.email,
                leaver.teamNumberOfMembers,
                leaver.teamTapBehaviour,
                tapLabel,
                rolesStr,
                ownerStr,
            ].filter(Boolean).join(' ').toLowerCase();
            return words.every((w) => haystack.includes(w));
        });
        setFilteredLeavers(filtered);
    }, [searchText, leavers]);

    const onSearch = (value) => setSearchText(value || '');
    const onDateRangeChange = (range) => setDateRange(range && range[0] && range[1] ? range : getCurrentWeekRange());

    const exportCsv = () => {
        const exportList = filteredLeavers || leavers || [];
        const getRoleStr = (leaver) => {
            const roles = [...(Array.isArray(leaver.roles) ? leaver.roles : [])];
            const isOwner = leaver.clientId && leaver.teamOwnerClientId && leaver.clientId === leaver.teamOwnerClientId;
            if (isOwner && !roles.some((r) => (r || '').toLowerCase() === 'owner')) {
                roles.unshift('owner');
            }
            return roles.join(',');
        };
        const rows = [
            ['id', 'memberId', 'clientId', 'displayName', 'email', 'avatar', 'teamId', 'teamName', 'teamOwnerClientId', 'teamTopLogo', 'teamNumberOfMembers', 'teamTapBehaviour', 'role', 'leftAt', 'createdAt', 'deletedAt'],
            ...exportList.map((leaver) => [
                leaver.id || '',
                leaver.memberId || leaver.id || '',
                leaver.clientId || '',
                leaver.displayName || '',
                leaver.email || '',
                leaver.avatar || '',
                leaver.teamId || '',
                leaver.teamName || '',
                leaver.teamOwnerClientId || '',
                leaver.teamTopLogo || '',
                leaver.teamNumberOfMembers ?? '',
                leaver.teamTapBehaviour || '',
                getRoleStr(leaver),
                toIsoString(leaver.updatedAt),
                toIsoString(leaver.createdAt),
                toIsoString(leaver.deletedAt),
            ]),
        ];
        const csv = rows.map((row) => row.map(escapeCsvValue).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `admin-team-leavers-${moment().format('YYYYMMDD-HHmmss')}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const displayList = filteredLeavers || leavers || [];
    const hasRows = displayList.length > 0;
    const resultCount = displayList.length;
    const atLimit = resultCount >= LIMIT;

    const columns = [
        {
            title: 'Member',
            key: 'member',
            render: (_, leaver) => {
                const clientHref = `${URLPath}/admin/clients/${leaver.clientId}`;
                const displayName = leaver.displayName || leaver.email || leaver.clientId;
                const isOwner = leaver.clientId && leaver.teamOwnerClientId && leaver.clientId === leaver.teamOwnerClientId;
                const rolesToShow = [...(Array.isArray(leaver.roles) ? leaver.roles : [])];
                if (isOwner && !rolesToShow.some((r) => (r || '').toLowerCase() === 'owner')) {
                    rolesToShow.unshift('owner');
                }
                const roleTags = rolesToShow.length > 0
                    ? rolesToShow.map((r) => (
                        <Tag key={r} color={ROLE_COLORS[(r || '').toLowerCase()] || 'default'}>
                            {r}
                        </Tag>
                    ))
                    : null;
                const leftLabel = leaver.updatedAt
                    ? moment(leaver.updatedAt.toMillis ? leaver.updatedAt.toMillis() : leaver.updatedAt).format('YYYY-MM-DD HH:mm')
                    : '-';
                return (
                    <Space direction="vertical" size={2}>
                        <Space wrap>
                            <Avatar
                                src={leaver.avatar}
                                icon={<UserOutlined />}
                                size={32}
                                style={{ flexShrink: 0 }}
                            />
                            <a href={clientHref}>{displayName}</a>
                            {roleTags}
                        </Space>
                        {leaver.email && leaver.email !== displayName && (
                            <span style={{ fontSize: 12, color: '#666', marginLeft: 40 }}>{leaver.email}</span>
                        )}
                        <Space size={6} style={{ fontSize: 12, marginLeft: 40 }}>
                            <CalendarOutlined />
                            <span>{leftLabel}</span>
                        </Space>
                    </Space>
                );
            },
        },
        {
            title: 'Team',
            key: 'team',
            render: (_, leaver) => {
                if (!leaver.teamId) return '-';
                const teamPageUrl = leaver.teamOwnerClientId
                    ? `${URLPath}/admin/clients/${leaver.teamOwnerClientId}/teams/${leaver.teamId}`
                    : null;
                const displayName = leaver.teamName || leaver.teamId;
                const memberCount = leaver.teamNumberOfMembers != null ? leaver.teamNumberOfMembers : null;
                const tapBehaviour = leaver.teamTapBehaviour || null;
                const tapLabel = tapBehaviour === 'team' ? 'pooled' : tapBehaviour;
                const tapColor = TIP_BEHAVIOUR_COLORS[tapBehaviour?.toLowerCase()] || TIP_BEHAVIOUR_COLORS[tapLabel?.toLowerCase()] || 'default';
                return (
                    <Space direction="vertical" size={2}>
                        <Space>
                            <Avatar
                                src={leaver.teamTopLogo}
                                icon={<TeamOutlined />}
                                shape="square"
                                size={32}
                                style={{ borderRadius: 6, flexShrink: 0 }}
                            />
                            {teamPageUrl ? (
                                <a href={teamPageUrl}>{displayName}</a>
                            ) : (
                                <span>{displayName}</span>
                            )}
                        </Space>
                        <Space size={4} style={{ fontSize: 12 }}>
                            {memberCount != null && (
                                <span>{memberCount} member{memberCount !== 1 ? 's' : ''}</span>
                            )}
                            {tapLabel && (
                                <Tag color={tapColor} style={{ marginRight: 0 }}>{tapLabel}</Tag>
                            )}
                        </Space>
                    </Space>
                );
            },
        },
    ];

    if (!isAdmin) {
        return <div>You are not authorized to view this page.</div>;
    }

    return (
        <div style={{ height: '100%', textAlign: 'center' }}>
            <PageHeader ghost={false} title="Team Leavers" />
            <Space direction="vertical" style={{ width: '100%', padding: 16 }}>
                <ClientsSearch initialValue={searchText} callbackOnSearch={onSearch} />
                <Space wrap>
                    <RangePicker
                        value={dateRange}
                        onChange={onDateRangeChange}
                        onOpenChange={(open) => { if (open) setPresetRefresh((t) => t + 1); }}
                        ranges={getAdminDatePresets()}
                        allowClear={false}
                    />
                    <span style={{ color: atLimit ? '#ff4d4f' : undefined }}>
                        {resultCount} result{resultCount !== 1 ? 's' : ''} (max {LIMIT})
                    </span>
                    <Button onClick={exportCsv} disabled={!hasRows}>Export CSV</Button>
                </Space>
                {loading ? (
                    <CenteredSpinner loading={true} />
                ) : (
                    <Table
                        dataSource={displayList}
                        columns={columns}
                        rowKey={(r) => `${r.teamId}-${r.memberId || r.clientId}`}
                        pagination={{ pageSize: 50 }}
                        scroll={{ x: 700 }}
                    />
                )}
            </Space>
        </div>
    );
}
